import React, {memo, useEffect} from 'react'
import ReactDiffViewer from 'react-diff-viewer'
import {isObject, isString, pick, startCase, uniq, uniqBy} from 'lodash'
import TimeAgo from 'react-timeago'
import {Accordion, Button, Dropdown, Grid, Header, Icon, Table, Message} from 'semantic-ui-react'
import {qs, userName} from '../../util'
import {withActivitiesData, withActivityData, withChildPathProps, withPropsPresent} from '../../hoc'
import {useSafeState} from '../../hook'

const _Activities = (
  {
    activities = [],
    activity,
    navigate,
    selectedServices = [],
    selectedUsers = [],
    selectedSubjects = [],
    selectedActions = [],
    showParents = ['service', 'version', 'contract', 'environment', 'resource', 'response']
  }
) => {

  const [state, setState] = useSafeState({
    activeIndex: 0,
    activities,
    visibleActivities: activities,
    selectedServices,
    selectedUsers,
    selectedSubjects,
    selectedActions,
    activity
  })

  useEffect(() => {
    const visibleActivities = getVisibleActivities({
      selectedServices,
      selectedUsers,
      selectedSubjects,
      selectedActions,
      activities
    })

    const activeIndex = activity ? visibleActivities.indexOf(visibleActivities.find(a => a.id === activity.id)) : -1
    setState({
      ...state,
      activeIndex,
      activities,
      visibleActivities,
      activity,
      selectedServices,
      selectedUsers,
      selectedSubjects,
      selectedActions
    })
  }, [activities, activity])

  const onSelectActivity = (e, titleProps) => {
    const {index} = titleProps
    const {activeIndex} = state
    const newIndex = activeIndex === index ? -1 : index
    setState({
      ...state,
      activeIndex: newIndex,
      activity: newIndex > -1 ? state.visibleActivities[newIndex] : state.activity
    })
    if (newIndex > -1) {
      const pathToNavTo = state.activity ? `../${state.visibleActivities[newIndex].id}` : state.visibleActivities[newIndex].id
      navigateWithQuery(pathToNavTo)
    } else {
      navigateWithQuery('../')
    }
  }

  const onRefClick = (e, path) => {
    e.preventDefault()
    navigate(path)
  }

  const onSelectFilter = (e, key, d) => {
    const updatedState = {...state, [key]: d.value}
    const visibleActivities = getVisibleActivities(updatedState)
    setState({...updatedState, visibleActivities})
    navigateWithQuery('/c/account/activity', updatedState)
  }

  const navigateWithQuery = (path, query) => {
    query = query || state
    const queryString = qs(pick(query, ['selectedServices', 'selectedUsers', 'selectedSubjects', 'selectedActions']))
    navigate(`${path}${queryString}`)
  }

  const isVisible = (value, selections) => {
    if (selections.length === 0) {
      return true
    }
    return selections.includes(value)
  }

  const getVisibleActivities = ({selectedServices, selectedUsers, selectedSubjects, selectedActions, activities = []}) => {
    const visibleActivities = activities.filter(a => {
      return isVisible(a.service.id, selectedServices)
        && isVisible(a.user.id, selectedUsers)
        && isVisible(a.action, selectedActions)
        && isVisible(a.subject, selectedSubjects)
    })
    return visibleActivities
  }

  const getServiceOptions = (activities) => {
    const services = activities.filter(a => a.service && a.service.name).map(a => a.service)
    return uniqBy(services, 'name').map((s) => {
      return {key: s.id, text: s.name, value: s.id}
    })
  }

  const getUserOptions = (activities) => {
    const users = activities.filter(a => a.user && a.user.email).map(a => a.user)
    return uniqBy(users, 'email').map((u) => {
      return {key: u.id, text: userName(u), value: u.id}
    })
  }

  const getSubjectOptions = (activities) => {
    const subjects = activities.map(a => a.subject)
    return uniq(subjects).map((s) => {
      return {key: s, text: startCase(s.toLowerCase()), value: s}
    })
  }

  const getActionOptions = (activities) => {
    const actions = activities.map(a => a.action)
    return uniq(actions).map((a) => {
      return {key: a, text: startCase(a.toLowerCase()), value: a}
    })
  }

  const canDisplay = (activity, attribute) => {
    return (showParents.includes(attribute) && isObject(activity[attribute]))
  }

  const getUserName = (activity) => {
    const {user, via} = activity
    if (via === 'API') {
      return 'API client'
    }
    if (via === 'CLI') {
      return 'CLI user'
    }
    return userName(user)
  }

  const getAction = (activity) => {
    const {action} = activity
    return action.toLowerCase()
  }

  const getMessage = (activity) => {
    const {subject, title, via} = activity
    let message = `${getUserName(activity)} ${getAction(activity)} ${subject.toLowerCase()}`
    if (title) {
      message = `${message} ${title}`
    }
    return `${message}
    via ${via === 'CONSOLE' ? via.toLowerCase() : via}`
  }

  const getNoRefsMessage = (action) => {
    const {subject} = action
    switch (subject) {
      case 'SERVICE':
        return 'This service was removed..'
      case 'VERSION':
        return `
    This
    version
    and
    its
    parent
    service
    were
    removed.`
      case 'CONTRACT':
        return `
    This
    contract
    and
    its
    parent
    version
    and
    service
    were
    removed.`
      case 'ENVIRONMENT':
        return `
    This
    environment
    and
    its
    parent
    version
    and
    service
    were
    removed.`
      case 'RESOURCE':
        return `
    This
    resource
    and
    its
    parent
    environment, version, and
    service
    were
    removed.`
      case 'RESPONSE':
        return `
    This
    response
    and
    its
    parent
    resource, environment, version, and
    service
    were
    removed.`
    }
  }


  if (!state.activities.length) {
    return (<div>No activity found</div>)
  }

  const serviceOptions = getServiceOptions(state.activities)
  const userOptions = getUserOptions(state.activities)
  const subjectOptions = getSubjectOptions(state.activities)
  const actionOptions = getActionOptions(state.activities)

  return (
    <Grid divided='vertically'>
      <Grid.Row columns='16'>
        <Grid.Column width='4'>
          <div style={{marginBottom: '10px'}}>
            <label><b>Service(s)</b></label>
          </div>
          <Dropdown placeholder='Filter by service(s)' fluid multiple search selection
                    disabled={serviceOptions.length === 0}
                    options={serviceOptions}
                    value={state.selectedServices}
                    data-testid='service-filters'
                    clearable
                    onChange={(e, d) => onSelectFilter(e, 'selectedServices', d)}/>
        </Grid.Column>
        <Grid.Column width='4'>
          <div style={{marginBottom: '10px'}}>
            <label><b>User(s)</b></label>
          </div>
          <Dropdown placeholder='Filter by user(s)' fluid multiple search selection
                    disabled={userOptions.length === 0}
                    options={userOptions}
                    value={state.selectedUsers}
                    data-testid='user-filters'
                    clearable
                    onChange={(e, d) => onSelectFilter(e, 'selectedUsers', d)}/>
        </Grid.Column>
        <Grid.Column width='4'>
          <div style={{marginBottom: '10px'}}>
            <label><b>Subjects(s)</b></label>
          </div>
          <Dropdown placeholder='Filter by subject(s)' fluid multiple search selection
                    disabled={subjectOptions.length === 0}
                    options={subjectOptions}
                    value={state.selectedSubjects}
                    data-testid='subject-filters'
                    clearable
                    onChange={(e, d) => onSelectFilter(e, 'selectedSubjects', d)}/>
        </Grid.Column>
        <Grid.Column width='4'>
          <div style={{marginBottom: '10px'}}>
            <label><b>Action(s)</b></label>
          </div>
          <Dropdown placeholder='Filter by action(s)' fluid multiple search selection
                    disabled={actionOptions.length === 0}
                    options={actionOptions}
                    value={state.selectedActions}
                    data-testid='action-filters'
                    onChange={(e, d) => onSelectFilter(e, 'selectedActions', d)}/>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns='16'>
        <Grid.Column width='16'>
          {
            state.visibleActivities.length > 0 && (
              <Accordion styled fluid>
                {
                  state.visibleActivities.map((a, i) => {
                    const titleProps = {
                      key: `activity_title_${i}`,
                      index: i,
                      active: state.activeIndex === i,
                      onClick: onSelectActivity
                    }
                    const contentProps = {
                      key: `activity_content_${i}`,
                      active: state.activeIndex === i
                    }
                    const canDisplayAtLeastOneReference = activity && activity.id === a.id && (
                      canDisplay(activity, 'service') ||
                      canDisplay(activity, 'version') ||
                      canDisplay(activity, 'contract') ||
                      canDisplay(activity, 'environment') ||
                      canDisplay(activity, 'resource') ||
                      canDisplay(activity, 'response'))


                    const original = a.diff && a.diff.original ? a.diff.original.join('\n') : undefined
                    const updated = a.diff && a.diff.updated ? a.diff.updated.join('\n') : undefined

                    return (
                      <React.Fragment key={`activity_${i}`}>
                        <Accordion.Title {...titleProps}>
                          <span data-test='activity-title'>
                            <Icon name='dropdown'/>
                            <span>{getMessage(a)}</span>
                            <TimeAgo date={a.createdAt} style={{float: 'right'}} live={false}/>
                          </span>
                        </Accordion.Title>
                        <Accordion.Content {...contentProps}>
                          {
                            state.activity && state.activity.id === a.id && (
                              <div data-test='activity-content'>
                                <Header as='h5'>References</Header>
                                {
                                  canDisplayAtLeastOneReference && (
                                    <Table definition>
                                      <Table.Body>
                                        {
                                          canDisplay(activity, 'service') && (
                                            <Table.Row data-test='activity-service'>
                                              <Table.Cell collapsing>Service</Table.Cell>
                                              <Table.Cell data-test='activity-service-button'>
                                                <Button basic size='small' icon labelPosition='right'
                                                        onClick={(e) => onRefClick(e, ` / c / services /${activity.service.id}/versions`)}>
                                                    <span
                                                      data-test='activity-service-name'>{activity.service.name}</span>
                                                  <Icon name='folder open outline'/>
                                                </Button>
                                              </Table.Cell>
                                            </Table.Row>
                                          )
                                        }
                                        {
                                          canDisplay(activity, 'version') && (
                                            <Table.Row data-test='activity-version'>
                                              <Table.Cell collapsing>Version</Table.Cell>
                                              <Table.Cell>
                                                <Button basic size='small' icon labelPosition='right'
                                                        data-test='activity-version-button'
                                                        onClick={(e) => onRefClick(e, `/c/services/${activity.service.id}/versions/${activity.version.id}/environments`)}>
                                                    <span
                                                      data-test='activity-version-name'>{activity.version.name}</span>
                                                  <Icon name='folder open outline'/>
                                                </Button>
                                              </Table.Cell>
                                            </Table.Row>
                                          )
                                        }
                                        {
                                          canDisplay(activity, 'contract') && (
                                            <Table.Row data-test='activity-version'>
                                              <Table.Cell collapsing>Contract</Table.Cell>
                                              <Table.Cell>
                                                <Button basic size='small' icon labelPosition='right'
                                                        data-test='activity-contract-button'
                                                        onClick={(e) => onRefClick(e, `/c/services/${activity.service.id}/versions/${activity.version.id}/contract`)}>
  <span
    data-test='activity-contract-name'>{`${activity.service.name} (${activity.version.name})`}</span>
                                                  <Icon name='folder open outline'/>
                                                </Button>
                                              </Table.Cell>
                                            </Table.Row>
                                          )
                                        }
                                        {
                                          canDisplay(activity, 'environment') && (
                                            <Table.Row data-test='activity-environment'>
                                              <Table.Cell collapsing>Environment</Table.Cell>
                                              <Table.Cell>
                                                <Button basic size='small' icon labelPosition='right'
                                                        data-test='activity-environment-button'
                                                        onClick={(e) => onRefClick(e, `/c/services/${activity.service.id}/versions/${activity.version.id}/environments/${activity.environment.id}/resources`)}>
  <span
    data-test='activity-environment-name'>{activity.environment.name}</span>
                                                  <Icon name='folder open outline'/>
                                                </Button>
                                              </Table.Cell>
                                            </Table.Row>
                                          )
                                        }
                                        {
                                          canDisplay(activity, 'resource') && (
                                            <Table.Row data-test='activity-resource'>
                                              <Table.Cell collapsing>Resource</Table.Cell>
                                              <Table.Cell
                                                title={`${activity.resource.verb.toUpperCase()} ${activity.resource.path}`}>
                                                <Button basic size='small' icon labelPosition='right'
                                                        data-test='activity-resource-button'
                                                        onClick={(e) => onRefClick(e, `/c/services/${activity.service.id}/versions/${activity.version.id}/environments/${activity.environment.id}/resources/${activity.resource.id}/responses`)}>
  <span
    data-test='activity-resource-name'>{`${activity.resource.verb.toUpperCase()} ${activity.resource.path.substring(0, 35)}${activity.resource.path.length > 35 ? '...' : ''}`}</span>
                                                  <Icon name='folder open outline'/>
                                                </Button>
                                              </Table.Cell>
                                            </Table.Row>
                                          )
                                        }
                                        {
                                          canDisplay(activity, 'response') && (
                                            <Table.Row data-test='activity-response'>
                                              <Table.Cell collapsing>Response</Table.Cell>
                                              <Table.Cell>
                                                <Button basic size='small' icon labelPosition='right'
                                                        data-test='activity-response-button'
                                                        onClick={(e) => onRefClick(e, `/c/services/${activity.service.id}/versions/${activity.version.id}/environments/${activity.environment.id}/resources/${activity.resource.id}/responses/${activity.response.id}/edit`)}>
  <span
    data-test='activity-response-name'>{`${activity.resource.verb.toUpperCase()} ${activity.resource.path.substring(0, 50)}${activity.resource.path.length > 50 ? '...' : ''}`}</span>
                                                  <Icon name='folder open outline'/>
                                                </Button>
                                              </Table.Cell>
                                            </Table.Row>
                                          )
                                        }
                                      </Table.Body>
                                    </Table>
                                  )
                                }
                                {
                                  !canDisplayAtLeastOneReference && (
                                    <p data-test='activity-subject-not-found'>{getNoRefsMessage(a)}</p>
                                  )
                                }
                              </div>
                            )
                          }
                          {
                            state.activity && state.activity.id === a.id && original && updated && original !== updated && (
                              <div data-test='activity-diff' style={{marginTop: '15px'}}>
                                <Header as='h5'>Changes</Header>
                                <ReactDiffViewer
                                  oldValue={original}
                                  newValue={updated}
                                  splitView={false}
                                />
                              </div>
                            )
                          }
                          {
                            state.activity && state.activity.id === a.id && original && updated && original === updated && (
                              <div data-test='activity-subject-not-changed' style={{marginTop: '15px'}}>
                                <Header as='h5'>Changes</Header>
                                <Message size='tiny'>None detected</Message>
                              </div>
                            )
                          }
                        </Accordion.Content>
                      </React.Fragment>
                    )
                  })
                }
              </Accordion>
            )
          }
          {
            state.visibleActivities.length === 0 && (
              <div data-testid='activities-not-found'>No activities found for current filters.</div>
            )
          }
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

let Activities = (props) => {
  const query = qs(props.location.search)
  query.selectedServices = isString(query.selectedServices) ? [query.selectedServices] : query.selectedServices
  query.selectedUsers = isString(query.selectedUsers) ? [query.selectedUsers] : query.selectedUsers
  query.selectedSubjects = isString(query.selectedSubjects) ? [query.selectedSubjects] : query.selectedSubjects
  query.selectedActions = isString(query.selectedActions) ? [query.selectedActions] : query.selectedActions
  props = {...props, ...query}
  return (
    <_Activities {...props}/>
  )
}

Activities = withPropsPresent(Activities, ['activities', 'activitiesLoaded', 'activityLoaded'])
Activities = withActivityData(Activities, 'Loading activity...')
Activities = withActivitiesData(Activities, 'Loading activities...')
Activities = withChildPathProps(Activities)
Activities = memo(Activities)

export {_Activities, Activities}

export default Activities